import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { RootState } from "@store/store";
import { useLazyGetTextosLegalesQuery } from "@api/configuration";
import { TextosLegales } from "@interfaces/catalogs";

/**
 * Textos legales de la empresa (config `TEXTOS_LEGALES` del tax system de la solicitud). Carga con respaldo: sin
 * config o si falla, `textosLegales` queda vacío y las pantallas usan los textos por defecto de `TypographyLegal`
 * (solo Sentry, sin modal). Mientras carga, `cargandoTextos` es `true` para mostrar un skeleton en lugar del texto
 * por defecto; sin tax system todavía (la sesión se está hidratando) sigue en carga hasta que llega.
 */
const useTextosLegales = (component: string) => {
  const taxSystemId = useSelector((state: RootState) => state.register.application.tax_system_id);
  const [getTextosLegales] = useLazyGetTextosLegalesQuery();
  const [textosLegales, setTextosLegales] = useState<TextosLegales>({});
  const [cargandoTextos, setCargandoTextos] = useState(true);

  const fetchTextosLegales = useCallback(async () => {
    if (!taxSystemId) return;
    try {
      setCargandoTextos(true);
      const response = await getTextosLegales({ tax_system_id: taxSystemId }).unwrap();
      setTextosLegales(response);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: `${component}-textos_legales` } });
    } finally {
      setCargandoTextos(false);
    }
  }, [taxSystemId, getTextosLegales, component]);

  useEffect(() => {
    fetchTextosLegales();
  }, [fetchTextosLegales]);

  return { textosLegales, cargandoTextos };
};

export default useTextosLegales;
